import React, { useEffect, useState, Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import Sidebar from './components/Layout/Sidebar';
import FormRenderer from './components/Forms/FormRenderer';
import { formConfig } from './config/formConfig';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import { motion } from 'framer-motion';

// Error Boundary to prevent white screens
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center">
          <div className="w-16 h-16 mb-6">
            <img src={formConfig.app.branding.logo} alt="BPA" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-2xl font-black text-slate-900 mb-2">Something went wrong.</h1>
          <p className="text-slate-500 mb-6">Please refresh or contact support.</p>
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mb-6 max-w-md overflow-auto">
            <pre className="text-xs text-slate-600 font-mono whitespace-pre-wrap">{this.state.error?.toString()}</pre>
          </div>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors"
          >
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const NotFound = () => {
  const location = useLocation();
  const isPublicPath = location.pathname.startsWith('/f/');
  
  return (
    <div className="min-h-screen bg-[var(--background)] flex flex-col items-center justify-center p-6 text-center">
      <div className="w-20 h-20 bg-white p-4 rounded-2xl shadow-sm border border-[var(--border)] flex items-center justify-center mb-8">
        <img src={formConfig.app.branding.logo} alt="BPA" className="w-full h-full object-contain" />
      </div>
      <h1 className="text-4xl font-black text-[var(--text)] mb-2">BPA Solutions</h1>
      <p className="text-[var(--textMuted)] mb-8 font-medium">Page not found.</p>
      {!isPublicPath ? (
        <Link 
          to="/hub"
          className="px-8 py-3 bg-[var(--primary)] text-white font-bold rounded-xl hover:opacity-90 transition-opacity"
        >
          Go to Hub
        </Link>
      ) : null}
    </div>
  );
};

const HubGate = ({ children }) => {
  const [accessKey, setAccessKey] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const requiredKey = import.meta.env[formConfig.access.hub.accessKeyEnvVar];

  useEffect(() => {
    const stored = localStorage.getItem('bpaHubKey');
    if (!requiredKey || stored === requiredKey) {
      setIsAuthenticated(true);
    }
  }, [requiredKey]);

  const handleAccess = (e) => {
    e.preventDefault();
    if (accessKey === requiredKey) {
      localStorage.setItem('bpaHubKey', accessKey);
      setIsAuthenticated(true);
    } else {
      alert('Invalid access key.');
    }
  };

  if (!isAuthenticated && requiredKey) {
    return (
      <div className="min-h-screen bg-[var(--background)] flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md bg-[var(--surface)] p-10 rounded-3xl border border-[var(--border)] shadow-2xl text-center">
          <div className="w-20 h-20 bg-white p-4 rounded-2xl shadow-sm border border-[var(--border)] flex items-center justify-center mx-auto mb-8">
            <img src={formConfig.app.branding.logo} alt="BPA" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-3xl font-black text-[var(--text)] mb-2">BPA Solutions</h1>
          <p className="text-[var(--textMuted)] mb-8 font-medium">Internal Forms Hub Access</p>
          <form onSubmit={handleAccess} className="space-y-4">
            <input
              type="password"
              placeholder="Enter Access Key"
              className="w-full bg-[var(--background)] border border-[var(--border)] rounded-xl px-4 py-3 text-center text-lg focus:ring-2 focus:ring-[var(--primary)] outline-none"
              value={accessKey}
              onChange={(e) => setAccessKey(e.target.value)}
            />
            <button className="w-full py-4 bg-[var(--primary)] text-white font-black rounded-xl hover:opacity-90 transition-opacity">
              Access Hub
            </button>
          </form>
        </div>
      </div>
    );
  }

  return children;
};

const PublicShareView = ({ routeId }) => {
  const location = useLocation();
  const route = formConfig.site.routes.find(r => r.id === routeId);
  const form = route ? formConfig.forms.find(f => f.id === route.targetId) : null;
  
  const searchParams = new URLSearchParams(location.search);
  const paramName = formConfig.access.publicShare.queryParam;
  const providedKey = searchParams.get(paramName);

  const BrandingWrap = ({ children }) => (
    <div className="min-h-screen bg-[var(--background)] flex flex-col items-center justify-center p-6 text-center">
      <div className="w-16 h-16 bg-white p-3 rounded-xl shadow-sm border border-[var(--border)] flex items-center justify-center mx-auto mb-6">
        <img src={formConfig.app.branding.logo} alt="BPA" className="w-full h-full object-contain" />
      </div>
      <h2 className="text-2xl font-black text-[var(--text)] mb-4">BPA Solutions</h2>
      <div className="p-8 bg-[var(--surface)] border border-[var(--border)] rounded-3xl shadow-xl w-full max-w-md">
        {children}
      </div>
    </div>
  );

  if (!route || !form) {
    return (
      <BrandingWrap>
        <p className="text-[var(--text)] font-bold text-lg mb-2">Unavailable</p>
        <p className="text-[var(--textMuted)] text-sm">This form is unavailable. Please request a new link.</p>
      </BrandingWrap>
    );
  }

  if (route.shareKey && providedKey !== route.shareKey) {
    return (
      <BrandingWrap>
        <div className="w-12 h-12 bg-[var(--danger)]/10 text-[var(--danger)] rounded-full flex items-center justify-center mx-auto mb-4">
          <SafeIcon name="AlertTriangle" className="text-2xl" />
        </div>
        <p className="text-[var(--text)] font-bold text-lg mb-2">Invalid Form Link</p>
        <p className="text-[var(--textMuted)] text-sm">This form link is not valid. Please request a new link.</p>
      </BrandingWrap>
    );
  }

  return <FormRenderer form={form} isPublicMode={true} route={route} />;
};

const PageRenderer = ({ page }) => (
  <div className="max-w-4xl mx-auto py-10">
    <h2 className="text-5xl font-black text-[var(--text)] mb-10 tracking-tighter">{page.title}</h2>
    <div className="space-y-8">
      {page.blocks.map((block, idx) => {
        if (block.type === 'paragraph') {
          return <p key={idx} className="text-xl text-[var(--textMuted)] leading-relaxed">{block.text}</p>;
        }
        if (block.type === 'bullets') {
          return (
            <ul key={idx} className="space-y-4">
              {block.items.map((item, i) => (
                <li key={i} className="flex items-start gap-4 text-lg text-[var(--text)]">
                  <div className="mt-2 w-2 h-2 rounded-full bg-[var(--primary)] shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          );
        }
        return null;
      })}
    </div>
  </div>
);

const AppContent = () => {
  const { toggleTheme, currentThemeId } = useTheme();
  const location = useLocation();
  
  // Public mode logic
  const isPublicMode = location.pathname.startsWith('/f/');

  useEffect(() => {
    const path = location.pathname;
    const currentRoute = formConfig.site.routes.find(r => 
      r.path === path || r.sharePath === path
    );

    if (currentRoute) {
      if (currentRoute.type === 'form') {
        const form = formConfig.forms.find(f => f.id === currentRoute.targetId);
        document.title = "BPA Solutions - " + (form ? form.name : "Form");
      } else if (currentRoute.type === 'page') {
        const page = formConfig.site.pages.find(p => p.id === currentRoute.targetId);
        document.title = "BPA Solutions - " + (page ? page.title : "Page");
      }
    } else {
      document.title = "BPA Solutions - Forms";
    }
  }, [location.pathname]);

  const headerBranding = (
    <div className="flex items-center gap-4">
      <div className="w-10 h-10 rounded-xl bg-white p-2 shadow-sm border border-[var(--border)] flex items-center justify-center">
        <img src={formConfig.app.branding.logo} alt="Logo" className="w-full h-full object-contain" />
      </div>
      <div className="flex items-center gap-3 text-[var(--textMuted)]">
        {!isPublicMode ? (
          <>
            <div className="p-2 rounded-lg bg-[var(--surfaceAlt)]">
              <SafeIcon name="Home" className="text-sm" />
            </div>
            <span className="opacity-30">/</span>
          </>
        ) : null}
        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text)]">BPA Solutions</span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--text)] selection:bg-[var(--primarySoft)] selection:text-[var(--primaryStrong)]">
      {!isPublicMode ? <Sidebar /> : null}
      <main className={isPublicMode ? "w-full" : "pl-[280px]"}>
        <header className="h-20 border-b border-[var(--border)] flex items-center justify-between px-12 sticky top-0 bg-[var(--background)]/80 backdrop-blur-xl z-50">
          {headerBranding}
          <div className="flex items-center gap-4">
            {formConfig.themes.themeSwitcher.enabled ? (
              <button
                onClick={toggleTheme}
                className="w-12 h-12 rounded-2xl bg-[var(--surfaceAlt)] text-[var(--text)] hover:scale-105 active:scale-95 transition-all flex items-center justify-center shadow-sm border border-[var(--border)]"
              >
                <SafeIcon name={currentThemeId === 'light' ? 'Moon' : 'Sun'} className="text-xl" />
              </button>
            ) : null}
            {!isPublicMode ? (
              <div className="w-10 h-10 rounded-full bg-[var(--primary)] text-white flex items-center justify-center font-black text-xs shadow-lg shadow-blue-500/20">
                AD
              </div>
            ) : null}
          </div>
        </header>

        <div className={isPublicMode ? "p-12 max-w-[900px] mx-auto" : "p-12"}>
          <Routes>
            {formConfig.site.routes.map(route => {
              const components = [];
              if (route.type === 'form') {
                const form = formConfig.forms.find(f => f.id === route.targetId);
                
                // Internal route (Hub)
                components.push(
                  <Route 
                    key={route.id} 
                    path={route.path} 
                    element={
                      <HubGate>
                        <FormRenderer form={form} isPublicMode={false} route={route} />
                      </HubGate>
                    } 
                  />
                );
                
                // Public share route
                if (route.sharePath) {
                  components.push(
                    <Route 
                      key={`${route.id}-share`} 
                      path={route.sharePath} 
                      element={<PublicShareView routeId={route.id} />} 
                    />
                  );
                }
              } else if (route.type === 'page') {
                const page = formConfig.site.pages.find(p => p.id === route.targetId);
                components.push(
                  <Route 
                    key={route.id} 
                    path={route.path} 
                    element={
                      <HubGate>
                        <PageRenderer page={page} />
                      </HubGate>
                    } 
                  />
                );
              }
              return components;
            })}
            
            <Route path="/hub" element={<HubGate><Navigate to="/website-intake" replace /></HubGate>} />
            <Route path="/" element={<Navigate to="/hub" replace />} />
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Router>
          <AppContent />
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;